import { Injectable } from '@angular/core';
import { Observable, firstValueFrom, map } from 'rxjs';
import {
  CatalogRecord,
  Doc,
  initInventoryOrderedItemRecord,
  initInventoryOrderItemMetricRecord,
  initInventoryOrderRecord,
  InStockItem,
  InventoryOrder,
  InventoryOrderContacts,
  InventoryOrderContactType,
  InventoryOrderedItem,
  InventoryOrderItemMetric
} from 'shared';
import { initInStockItemRecord } from 'shared/dist/inventory/in-stock-item';
import { DbService } from './../../services/db.service';
import {
  arrayUnion,
  limit,
  orderBy,
  QueryConstraint,
  startAfter,
  Timestamp,
  where
} from '@angular/fire/firestore';

@Injectable({
  providedIn: 'root'
})
export class InventoryService {
  constructor(private db: DbService) {}

  createInStockItem(record: CatalogRecord, id?: string): Promise<InStockItem> {
    const item = this.convertCatalogToInStockItem(record);
    return this.db.create<InStockItem>(item, id);
  }

  deleteInStockItem(record: InStockItem): Promise<void> {
    return this.db.delete<InStockItem>(record);
  }

  getInStockItems(
    searchTerm: string = '',
    startAfterProvided: any = '',
    limitProvided = 5
  ): Observable<InStockItem[]> {
    const constraints: QueryConstraint[] = [];
    if (searchTerm) {
      constraints.push(where('searchTerms', 'array-contains', searchTerm.toLowerCase()));
    }
    constraints.push(orderBy('createdAt', 'asc'));
    if (startAfterProvided) {
      constraints.push(startAfter(startAfterProvided));
    }
    constraints.push(limit(limitProvided));
    return this.db.queryCollection$<InStockItem>(
      'in-stock-items',
      constraints,
      initInStockItemRecord
    );
  }

  async batchRenewItems(since?: Date, until: Date = new Date()) {
    const constraints: QueryConstraint[] = [
      where('createdAt', '>=', since),
      where('createdAt', '<=', until)
    ];
    const res = await firstValueFrom(
      this.db.queryCollection$<InStockItem>('in-stock-items', constraints, initInStockItemRecord)
    );
    const docs: Doc[] = res.map((r) => {
      return this.convertCatalogToInStockItem(r);
    });
    // break up batches
    let count = 0;
    let batch = [];
    for (const doc of docs) {
      if (count % 100 === 0) {
        await this.db.batchUpdate(batch);
        batch = [];
      }
      batch.push(doc);
      count++;
    }
    return this.db.batchUpdate(batch);
  }

  private convertCatalogToInStockItem(record: CatalogRecord | InStockItem): InStockItem {
    delete record.searchTerms;
    delete record.collection;
    // delete record.wholesalePrice;
    // delete record.retailPrice;
    delete record.updatedAt;
    delete record.updatedBy;
    delete record.createdAt;
    delete record.createdBy;
    // fix name for alphabetizing
    let sizing = '';
    let name = '';
    const split = record.name.split(' ');
    split.forEach((seg) => {
      if (seg.length > 0 && /[a-zA-Z]/.test(seg.charAt(0))) {
        name += seg + ' ';
      } else {
        sizing += seg + ' ';
      }
    });
    record.name = (name + sizing).trim();
    return initInStockItemRecord(record);
  }

  createInventoryOrder(order: InventoryOrder): Promise<InventoryOrder> {
    const prepped = initInventoryOrderRecord(order);
    return this.db.create<InventoryOrder>(prepped);
  }

  updateInventoryOrder(order: InventoryOrder): Promise<InventoryOrder> {
    const prepped = initInventoryOrderRecord({
      ...order,
      createdAt: this.db.convertTimeObjectToTimestamp(order.createdAt)
    });
    // structured clone from editing changes date to object
    return this.db.update<InventoryOrder>(prepped);
  }

  deleteInventoryOrder(order: InventoryOrder): Promise<void> {
    return this.db.delete<InventoryOrder>(order);
  }

  getInventoryOrders$(
    daysAgo: number,
    isProcessed,
    orderByProvided: 'asc' | 'desc' = 'asc',
    limitProvided = 10
  ): Observable<InventoryOrder[]> {
    const constraints: QueryConstraint[] = [];
    constraints.push(where('isProcessed', '==', isProcessed));
    constraints.push(
      where('date', '>=', new Date(new Date().setDate(new Date().getDate() - daysAgo)))
    );
    constraints.push(orderBy('date', orderByProvided));
    constraints.push(limit(limitProvided));
    return this.db
      .queryCollection$<InventoryOrder>('inventory-orders', constraints, initInventoryOrderRecord)
      .pipe(
        map((orders) => {
          return orders.map((order) => {
            order.date = order.date instanceof Timestamp ? order.date.toDate() : order.date;
            return order;
          });
        })
      );
  }

  getInventoryOrderByDate$(date: Date): Observable<InventoryOrder[]> {
    const constraints: QueryConstraint[] = [];
    constraints.push(where('isProcessed', '==', false));
    constraints.push(where('date', '==', date));
    return this.db
      .queryCollection$<InventoryOrder>('inventory-orders', constraints, initInventoryOrderRecord)
      .pipe(
        map((orders) => {
          return orders.map((order) => {
            order.date = order.date instanceof Timestamp ? order.date.toDate() : order.date;
            return order;
          });
        })
      );
  }

  getOrderedItems$(searchTerm?: string): Observable<InventoryOrderedItem[]> {
    const constraints: QueryConstraint[] = [];
    if (searchTerm) {
      constraints.push(
        where('searchTerms', 'array-contains-any', [searchTerm, searchTerm.toLowerCase()])
      );
    }
    constraints.push(
      ...[
        where('date', '>=', new Date(new Date().setDate(new Date().getDate() - 30))),
        orderBy('date', 'desc')
      ]
    );
    return this.db
      .queryCollection$<InventoryOrderedItem>(
        'inventory-ordered-items',
        constraints,
        initInventoryOrderedItemRecord
      )
      .pipe(
        map((orders) => {
          return orders.map((order) => {
            order.date = order.date instanceof Timestamp ? order.date.toDate() : order.date;
            return order;
          });
        })
      );
  }

  getInventoryItemMetrics$(
    keyword: string,
    name: string,
    isLatest: boolean,
    limitProvided: number
  ): Observable<InventoryOrderItemMetric[]> {
    const constraints: QueryConstraint[] = [];
    if (isLatest) {
      constraints.push(where('isLatest', '==', isLatest));
    }
    if (keyword) {
      constraints.push(where('searchTerms', 'array-contains', keyword.toLowerCase()));
    }
    if (name) {
      constraints.push(where('name', '==', name));
    }
    constraints.push(orderBy('timestamp', 'asc'));
    constraints.push(limit(limitProvided));

    return this.db
      .queryCollection$<InventoryOrderItemMetric>(
        'inventory-order-item-metrics',
        constraints,
        initInventoryOrderItemMetricRecord
      )
      .pipe(
        map((metrics) => {
          return metrics
            .map((m) => {
              m.timestamp = m.timestamp instanceof Timestamp ? m.timestamp.toDate() : m.timestamp;
              return m;
            })
            .filter((m) => {
              if (!isLatest) {
                // dont include latest if not req.
                return m.isLatest ? false : true;
              }
              return m;
            });
        })
      );
  }

  getInventoryOrderContacts$(): Observable<InventoryOrderContacts> {
    return this.db.doc$<InventoryOrderContacts>('inventory-order-contacts/MAIN');
  }

  addInventoryOrderContact(name: string, type: InventoryOrderContactType): Promise<void> {
    return this.db.updatePartial('inventory-order-contacts/MAIN', {
      [type + 's']: arrayUnion({ name })
    });
  }
}
